/**
 * ProductCountRow — renders a single product's counting UI in inventory.
 * Handles primary/secondary unit inputs with +/- buttons.
 */
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ArrowUp, ArrowDown, Pencil, Trash } from "@phosphor-icons/react";

export function ProductCountRow({
  product, idx, counts, total, value, hasValue, unitInfo,
  isAdmin, searchQuery,
  onIncrement, onDecrement, onChange,
  onMoveProduct, onEditProduct, onDeleteProduct,
  parseUnitString, getUnitLabel
}) {
  return (
    <div
      data-testid={`product-row-${product.product_id}`}
      className="p-4 bg-muted/30 rounded-lg"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground font-mono">#{idx + 1}</span>
            <p className="font-medium truncate">{product.name}</p>
            {product.price > 0 && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                ${product.price?.toFixed(2)}/{unitInfo.primaryUnit}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{product.unit}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && !searchQuery && (
            <div className="flex items-center gap-1 mr-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onMoveProduct(product, "up")}><ArrowUp size={14} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onMoveProduct(product, "down")}><ArrowDown size={14} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEditProduct(product)}><Pencil size={14} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDeleteProduct(product.product_id)}><Trash size={14} /></Button>
            </div>
          )}
          {hasValue && (
            <div className="text-right min-w-[100px]">
              <p className="text-lg font-semibold tabular-nums text-primary">{total} {unitInfo.primaryUnit}</p>
              {product.price > 0 && (
                <p className="text-sm font-medium tabular-nums text-green-600">${value}</p>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col">
          <Label className="text-xs text-muted-foreground mb-1">
            {unitInfo.primaryUnit} ({getUnitLabel(unitInfo.primaryUnit)})
          </Label>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => onDecrement(product.product_id, 'primary')}>-</Button>
            <Input
              type="number"
              value={counts.primary || ""}
              onChange={(e) => onChange(product.product_id, 'primary', e.target.value)}
              className="text-center h-12 text-lg tabular-nums"
              placeholder="0" min="0" step="0.5"
            />
            <Button variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => onIncrement(product.product_id, 'primary')}>+</Button>
          </div>
        </div>
        {unitInfo.secondaryUnit && (
          <div className="flex flex-col">
            <Label className="text-xs text-muted-foreground mb-1">
              {unitInfo.secondaryUnit} ({getUnitLabel(unitInfo.secondaryUnit)})
              <span className="text-[10px] ml-1">({unitInfo.conversion} per {unitInfo.primaryUnit})</span>
            </Label>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => onDecrement(product.product_id, 'secondary')}>-</Button>
              <Input
                type="number"
                value={counts.secondary || ""}
                onChange={(e) => onChange(product.product_id, 'secondary', e.target.value)}
                className="text-center h-12 text-lg tabular-nums"
                placeholder="0" min="0" step={unitInfo.secondaryUnit === 'LB' ? "0.1" : "1"}
              />
              <Button variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => onIncrement(product.product_id, 'secondary')}>+</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
